import { dataFeatureSchema, type DataLayer } from '../shared/data-layer.js';
import { validateLocalDataLayerPayload } from './local-data-layer-limits.js';
import { datasetWriter, retainDataset } from './local-dataset-store.js';
import { z } from 'zod';
import type { SchemaInput } from '../shared/schema-input.js';

/** Validate old drafts a chunk at a time and publish a new reference after success. */
export async function restoreStoredFeatures(metadata: SchemaInput, batches: AsyncIterable<SchemaInput[]>, dataset?: DataLayer['dataset']): Promise<DataLayer | null> {
  const raw = z.record(z.string(), z.unknown()).parse(metadata);
  const migrate = !dataset && z.number().parse(raw.featureCount) > 10000;
  const bounded = Boolean(dataset || migrate);
  const envelope = { ...raw, featureCount: 0, bounds: null };
  const base = validateLocalDataLayerPayload({ version: 1, layer: envelope, features: [] });
  if (!base.success) return null;
  let layer: DataLayer = { ...base.payload.layer, features: [], saved: false };
  const writer = migrate ? await datasetWriter() : null;
  try {
    for await (const batch of batches) for (const value of batch) {
      const feature = dataFeatureSchema.parse(value);
      if (writer) await writer.append(feature); else layer.features.push(feature);
    }
    if (writer) layer = await writer.finish(layer);
    else if (dataset) {
      await retainDataset(dataset);
      layer.dataset = dataset; layer.featureCount = dataset.count;
      layer.bounds = z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable().parse(raw.bounds);
    } else {
      const validated = validateLocalDataLayerPayload({ version: 1, layer: raw, features: layer.features }, { reuseFeatures: true });
      if (!validated.success) return null;
      layer = { ...validated.payload.layer, features: validated.payload.features, saved: false };
    }
    if (bounded && layer.features.length) throw new Error('A stored dataset reference contains inline records.');
    return layer;
  } finally { await writer?.close(); }
}
